import { BorderRadius, Color, Spacing } from '@persona-id/design-tokens';
import { CircleXInverseIcon } from '@persona-id/design-tokens/icons';
import {
  cloneElement,
  ComponentPropsWithoutRef,
  forwardRef,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import Button, { ButtonProps } from 'design-system/components/Button';
import useFormField, {
  FormControlContext,
} from 'design-system/components/FormControl/useFormField';
import inputMixin, { InputMixinProps, InputSizes } from 'design-system/mixins/input';

const Container = styled.div`
  display: flex;
  position: relative;
`;

const LeftIcon = styled.div<{ $size?: InputSizes }>`
  position: absolute;
  left: ${Spacing[2]};
  top: 50%;
  transform: translateY(-50%);
  margin: 0;
  font-size: 0px;
  color: ${Color.BorderIconSecondary};
  height: 16px;
  width: 16px;

  svg {
    height: 100%;
    width: 100%;
  }
`;

const InputButtonContainer = styled.div<{ $rightButton?: InputGroupProps['rightButton'] }>`
  position: absolute;
  display: flex;
  gap: ${Spacing[2]};
  right: ${({ $rightButton }) => ($rightButton ? Spacing[1] : Spacing[2])};
  top: 50%;
  transform: translateY(-50%);
  margin: 0;
  font-size: 0px;
`;

export const ClearButton = styled(Button).attrs({
  width: 'icon',
  variant: 'basic',
})`
  background-color: transparent;
  padding: 0;
  height: auto;
  width: auto;
  border-radius: ${BorderRadius.Circle};
  color: ${Color.BorderIconSecondary};

  ${({ disabled }) =>
    disabled &&
    css`
      color: ${Color.BorderIconDisabled};
    `}
`;

interface StyledInputProps extends InputMixinProps {
  $leftIcon?: ReactNode;
  $onClear?: MouseEventHandler<HTMLButtonElement>;
}

export interface InputProps
  extends Omit<ComponentPropsWithoutRef<'input'>, 'size'>,
    Omit<InputMixinProps, '$size'> {
  /**
   * Size of the input. If you need the HTML size attr, use inputSize.
   */
  size?: InputSizes;

  /**
   * `size` HTML attr on `<input>`
   */
  inputSize?: ComponentPropsWithoutRef<'input'>['size'];

  leftIcon?: StyledInputProps['$leftIcon'];
  onClear?: StyledInputProps['$onClear'];
}

const InputBase = styled.input<StyledInputProps>`
  ${inputMixin};
  line-height: 1;
  ${({ $size, $leftIcon, $onClear }) => {
    let btnSpacing: string;
    switch ($size) {
      default:
      case 'md':
        btnSpacing = `calc(${Spacing[3]} + 16px + ${Spacing[1]})`;
        return css`
          padding-right: ${$onClear ? btnSpacing : Spacing[3]};
          padding-left: ${$leftIcon ? btnSpacing : Spacing[3]};
        `;
      case 'sm':
        btnSpacing = `calc(${Spacing[2]} + 16px + ${Spacing[1]})`;
        return css`
          padding-right: ${$onClear ? btnSpacing : Spacing[2]};
          padding-left: ${$leftIcon ? btnSpacing : Spacing[2]};
        `;
    }
  }}
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size, leftIcon, onClear, id, hasWarning, hasError, disabled, maxLength, ...props },
  ref,
) {
  const field = useFormField({ hasWarning, hasError, disabled, inputId: id, maxLength });

  return (
    <InputBase
      $size={size}
      $leftIcon={leftIcon}
      $onClear={onClear}
      ref={ref}
      id={field.inputId}
      aria-describedby={`${field.helperTextId}${field.hasWarning ? ` ${field.warningTextId}` : ''}`}
      aria-errormessage={field.hasError ? field.errorTextId : undefined}
      aria-invalid={field.hasError}
      hasWarning={field.hasWarning}
      hasError={field.hasError}
      disabled={field.disabled}
      maxLength={field.maxLength}
      {...props}
      size={props.inputSize}
    />
  );
});

export interface InputGroupProps
  extends ComponentPropsWithoutRef<'div'>,
    Omit<InputMixinProps, '$size'> {
  /**
   * Input
   */
  children: ReactElement<InputProps>;

  /**
   * Size of the input. If you need the HTML size attr, use inputSize.
   */
  size?: InputSizes;

  /**
   * Icon to render to the left of the input field.
   *
   * Import icon from `@persona-id/design-tokens/icons`. Use 24px for `lg` input size.
   */
  leftIcon?: ReactNode;

  /**
   * Renders clear button to the right and calls function upon click.
   */
  onClear?: MouseEventHandler<HTMLButtonElement>;

  /**
   * Button to be rendered to the right of the input
   */
  rightButton?: ReactElement<ButtonProps>;

  /**
   * Disabled prop will be passed down to `<Input>`
   */
  disabled?: boolean;
}

export const InputGroup = forwardRef<HTMLDivElement, InputGroupProps>(function InputGroup(
  {
    size = 'md',
    leftIcon,
    onClear,
    rightButton,
    children,
    disabled,
    hasWarning,
    hasError,
    ...props
  },
  ref,
) {
  const field = useFormField({ hasWarning, hasError, disabled });
  const inputButtonContainerRef = useRef<HTMLDivElement>();
  const [inputPaddingRight, setInputPaddingRight] = useState<string | null>(null);

  useEffect(() => {
    // We only need to calculate padding in presence of the right button
    if (rightButton && inputButtonContainerRef.current) {
      setInputPaddingRight(`${12 + inputButtonContainerRef.current.offsetWidth + 4}px`);
    }
  }, [setInputPaddingRight]);

  return (
    <FormControlContext.Provider value={field}>
      <Container ref={ref} {...props}>
        {leftIcon && <LeftIcon $size={size}>{leftIcon}</LeftIcon>}
        {cloneElement(children, {
          size,
          leftIcon: leftIcon,
          ...(rightButton ? { rightButton } : {}),
          onClear: onClear,
          style: inputPaddingRight
            ? {
                paddingRight: inputPaddingRight,
              }
            : {},
        })}
        {(onClear || rightButton) && ( // TODO: Render only if value is present
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          <InputButtonContainer ref={inputButtonContainerRef} $rightButton={rightButton}>
            {onClear && (
              <ClearButton onClick={onClear} disabled={field.disabled} aria-label='Clear'>
                <CircleXInverseIcon />
              </ClearButton>
            )}
            {rightButton &&
              cloneElement(rightButton, {
                size: 'sm',
                width: rightButton.props.width || 'auto',
              })}
          </InputButtonContainer>
        )}
      </Container>
    </FormControlContext.Provider>
  );
});

export default Input;
